import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  ChevronDown, 
  ChevronRight,
  LayoutDashboard,
  Store,
  Users,
  TrendingUp,
  MessageSquare,
  Megaphone,
  Settings,
  HelpCircle,
  PlayCircle,
  BookOpen,
  ArrowLeft,
  Sparkles,
  BarChart3,
  PieChart,
  Target,
  Wallet,
  RefreshCw,
  GraduationCap,
  Video
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { cn } from '../lib/utils';
import { TrainingMenu } from '../components/TrainingModule';

const helpSections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: PlayCircle,
    color: 'text-green-500',
    articles: [
      {
        id: 'platform-overview',
        title: 'Platform Overview',
        content: `
## Welcome to Fundle Brain

Fundle Brain is an AI-powered loyalty analytics platform that helps you understand your customers, optimize your loyalty programs, and drive business growth.

### Key Features
- **Real-time Dashboards**: Monitor KPIs, revenue, and customer behavior
- **AI Conversation**: Ask questions in natural language and get instant insights
- **Campaign Builder**: Create and schedule SMS/WhatsApp campaigns
- **Custom Reports**: Build and export tailored reports

### Navigation
- Use the sidebar to navigate between different sections
- Click the "?" icon anytime to access this Help Center
- Use the guided tour for interactive walkthroughs
        `
      },
      {
        id: 'first-login',
        title: 'Your First Login',
        content: `
## Logging Into Fundle Brain

### For Brand Users
1. Visit your brand's login page
2. Enter your email and password provided by your admin
3. You'll be directed to your brand's dashboard

### For Super Admins
1. Use the super admin credentials
2. Access all brands and platform settings
3. Manage users, brands, and configurations

### Password Reset
Contact your administrator if you need to reset your password.
        `
      },
      {
        id: 'navigation-basics',
        title: 'Navigation Basics',
        content: `
## Navigating the Platform

### Sidebar Menu
The left sidebar contains all main sections:
- **Command Center**: Overview dashboard with key metrics
- **Executive Dashboard**: High-level business insights
- **Store Performance**: Detailed store analytics
- **Customer Analytics**: Cohort and migration analysis
- **RFM & Churn**: Customer segmentation
- **Points Economy**: Loyalty points tracking
- **AI Conversation**: Natural language analytics
- **Campaign Builder**: Marketing campaigns

### Quick Actions
- Click the logo to return to the main dashboard
- Use keyboard shortcut \`Ctrl/Cmd + K\` for quick search
- Toggle dark/light mode from settings
        `
      }
    ]
  },
  {
    id: 'dashboards',
    title: 'Dashboards',
    icon: LayoutDashboard,
    color: 'text-blue-500',
    articles: [
      {
        id: 'command-center',
        title: 'Command Center',
        content: `
## Command Center Dashboard

The Command Center is your operational headquarters, providing real-time visibility into your loyalty program's performance.

### Key Metrics
- **Total Members**: Count of registered loyalty members
- **Active Members**: Members who transacted recently
- **Total Revenue**: Sum of all transactions
- **Average Transaction Value (ATV)**: Revenue per transaction

### Charts & Visualizations
- **Monthly Member Growth**: Track enrollment trends
- **Revenue by Store**: Compare store performance
- **Tier Distribution**: See membership tier breakdown

### Filters
- **Date Range**: Select custom time periods
- **Store**: Filter by specific locations
- **City**: Analyze by geographic region

### AI Insights
Click "Generate Insights" to get AI-powered recommendations based on your current data.
        `
      },
      {
        id: 'store-performance',
        title: 'Store Performance',
        content: `
## Store Performance Dashboard

Analyze individual store metrics and compare performance across your network.

### Store Metrics
- **Revenue**: Total sales per store
- **Bills**: Transaction count
- **ATV**: Average transaction value
- **Member Share**: % of transactions from loyalty members
- **Repeat Rate**: % of customers who return

### Features
- **Store Search**: Find specific stores quickly
- **City Filter**: Group stores by location
- **Drill-Down**: Click any store for detailed analysis
- **Export**: Download store data as CSV

### Drill-Down View
Click any store row to see:
- Daily/weekly/monthly trends
- Top customers at that store
- Peak hours analysis
- Product category breakdown
        `
      },
      {
        id: 'rfm-churn',
        title: 'RFM & Churn Analysis',
        content: `
## RFM & Churn Dashboard

Segment your customers based on Recency, Frequency, and Monetary value.

### RFM Segments
- **Champions**: Recent, frequent, high spenders
- **Loyal Customers**: Consistent buyers
- **Potential Loyalists**: Recent customers with growth potential
- **At Risk**: Previously active, now declining
- **Hibernating**: Long inactive customers
- **Lost**: No activity for extended period

### Using RFM Data
1. Identify which segments need attention
2. Create targeted campaigns for each segment
3. Track segment migration over time

### Churn Prediction
- View customers likely to churn
- Understand churn risk factors
- Take proactive retention actions
        `
      },
      {
        id: 'cohort-migration',
        title: 'Cohort & Migration',
        content: `
## Cohort & Migration Dashboard

Track how customer groups evolve over time.

### Cohort Analysis
Customers grouped by their first transaction month:
- See retention rates by cohort
- Compare cohort performance
- Identify acquisition quality trends

### Migration Patterns
- **One-Timer to Repeat**: Conversion tracking
- **Tier Upgrades/Downgrades**: Loyalty progression
- **Revenue Contribution**: Value by cohort

### VN-Style Analysis
Our cohorts follow the VN methodology:
- One-timers: Above/Below ATV
- Repeat customers: Visit frequency bands
- Recency: 0-3M, 3-6M, 6-12M, 12-24M, 24M+
        `
      },
      {
        id: 'points-economy',
        title: 'Points Economy',
        content: `
## Points Economy Dashboard

Monitor your loyalty program's points ecosystem.

### Key Metrics
- **Points Issued**: Total points given to customers
- **Points Redeemed**: Points used for rewards
- **Points Outstanding**: Unredeemed balance (liability)
- **Earn-Burn Ratio**: Redemption health indicator
- **Breakage**: Expected unredeemed points

### Charts
- **Monthly Earn vs Burn**: Track point flow
- **Redemption Trends**: See reward usage patterns
- **Points by Tier**: Distribution across membership levels

### Health Indicators
- Green: Healthy earn-burn ratio (0.3-0.5)
- Yellow: Moderate imbalance
- Red: Requires attention (too high/low redemption)
        `
      }
    ]
  },
  {
    id: 'ai-features',
    title: 'AI Features',
    icon: Sparkles,
    color: 'text-purple-500',
    articles: [
      {
        id: 'ai-conversation',
        title: 'AI Conversation',
        content: `
## AI Conversation

Chat with your data using natural language queries.

### How to Use
1. Navigate to "AI Conversation" in the sidebar
2. Click "Start New Conversation" or select a suggested prompt
3. Type your question in plain English
4. Receive instant insights with tables and visualizations

### Example Questions
- "What's my total revenue this month?"
- "Show me top 25 stores by member count"
- "How many repeat customers do I have?"
- "What's my RFM segmentation breakdown?"
- "Which stores are underperforming?"

### Tips
- Be specific with your questions
- Mention time periods for accurate data
- Ask follow-up questions for deeper analysis
- Use "compared to last month" for trends

### Chat History
- All conversations are saved
- Access previous chats from the sidebar
- Continue conversations anytime
        `
      },
      {
        id: 'ai-insights',
        title: 'AI-Generated Insights',
        content: `
## AI Insights

Every dashboard includes AI-powered insights.

### How It Works
1. Click "Generate Insights" on any dashboard
2. AI analyzes your current data and filters
3. Receive actionable recommendations

### Insight Types
- **Trends**: Identify patterns in your data
- **Anomalies**: Spot unusual behavior
- **Opportunities**: Find growth areas
- **Risks**: Highlight potential issues

### Acting on Insights
- Each insight includes recommended actions
- Click through to relevant sections
- Create campaigns based on suggestions
        `
      }
    ]
  },
  {
    id: 'campaigns',
    title: 'Campaigns',
    icon: Megaphone,
    color: 'text-orange-500',
    articles: [
      {
        id: 'campaign-builder',
        title: 'Campaign Builder',
        content: `
## Campaign Builder

Create and manage marketing campaigns for your loyalty members.

### Creating a Campaign
1. Navigate to "Campaign Builder"
2. Click "Create New Campaign"
3. Select campaign type (SMS/WhatsApp)
4. Define your target audience
5. Write your message
6. Schedule or send immediately

### Audience Selection
- **All Members**: Broadcast to everyone
- **By Segment**: Target RFM segments
- **By Store**: Location-based campaigns
- **Custom Filter**: Advanced targeting

### Message Templates
- Use merge tags: {name}, {points}, {tier}
- Preview before sending
- A/B test different messages

### Scheduling
- Send immediately
- Schedule for future date/time
- Set up recurring campaigns
        `
      },
      {
        id: 'campaign-analytics',
        title: 'Campaign Analytics',
        content: `
## Campaign ROI Dashboard

Track campaign performance and ROI.

### Metrics Tracked
- **Sent**: Messages delivered
- **Opened**: Open rate (for supported channels)
- **Clicked**: Link click-through rate
- **Converted**: Resulting transactions
- **Revenue**: Attributed sales

### ROI Calculation
ROI = (Revenue - Campaign Cost) / Campaign Cost × 100

### Optimization Tips
- Compare performance across campaigns
- Identify best-performing segments
- Optimize send times
- Refine messaging based on data
        `
      }
    ]
  },
  {
    id: 'admin',
    title: 'Administration',
    icon: Settings,
    color: 'text-gray-500',
    articles: [
      {
        id: 'user-management',
        title: 'User Management',
        content: `
## User Management

Manage users and permissions (Admin only).

### User Roles
- **Super Admin**: Full platform access
- **Brand Admin**: Full access to assigned brand
- **Brand User**: Limited access based on permissions

### Creating Users
1. Go to Settings > User Management
2. Click "Add User"
3. Enter email and name
4. Select role and brand
5. Configure section access
6. Save and send invite

### Permissions
Control which sections each user can access:
- Command Center
- Store Performance
- AI Conversation
- Campaign Builder
- And more...

### Deactivating Users
- Toggle user status to inactive
- User loses access immediately
- Can be reactivated anytime
        `
      },
      {
        id: 'brand-management',
        title: 'Brand Management',
        content: `
## Brand Management

Configure brands (Super Admin only).

### Brand Settings
- **Name**: Display name
- **Logo**: Brand logo URL
- **Subdomain**: Custom URL
- **Theme**: Primary/secondary colors
- **API Key**: Data Bridge connection

### Creating a Brand
1. Go to Super Admin > Brand Management
2. Click "Create Brand"
3. Fill in brand details
4. Configure data connection
5. Create initial admin user

### Data Connection
Each brand connects to its data source via:
- Data Bridge URL (global setting)
- Brand-specific API key
- Sync runs every 15 minutes
        `
      },
      {
        id: 'data-sync',
        title: 'Data Sync',
        content: `
## Data Synchronization

Understanding how data flows into Fundle Brain.

### Sync Process
1. External data source (your database)
2. Data Bridge API (middleware)
3. Fundle Brain local storage
4. Dashboard queries

### Sync Schedule
- Automatic sync every 15 minutes
- Manual sync available for admins
- View sync history in settings

### Data Freshness
- Dashboards show sync timestamp
- "Fresh" indicator when data is recent
- AI insights use latest synced data

### Troubleshooting
- Check sync status in admin panel
- Verify API key configuration
- Contact support for persistent issues
        `
      }
    ]
  }
];

const faqItems = [
  {
    question: "How often is dashboard data updated?",
    answer: "Data is synchronized from your source every 15 minutes. You can see the last sync time at the top of each dashboard."
  },
  {
    question: "Can I export data from the dashboards?",
    answer: "Yes! Most dashboards have an export option. Look for the download icon to export data as CSV or Excel."
  },
  {
    question: "Why is the AI Conversation not responding?",
    answer: "Ensure you have an active thread selected. Click 'Start New Conversation' if needed. If issues persist, check your internet connection or contact support."
  },
  {
    question: "How do I change my password?",
    answer: "Contact your administrator to reset your password. Self-service password reset is coming soon."
  },
  {
    question: "What does the 'At Risk' segment mean in RFM?",
    answer: "At Risk customers were previously active but haven't transacted recently. They need re-engagement campaigns to prevent churn."
  },
  {
    question: "Can I create custom dashboards?",
    answer: "Use the Report Builder to create custom reports with your preferred metrics. Full custom dashboards are on our roadmap."
  },
  {
    question: "How do I target specific customers for campaigns?",
    answer: "In Campaign Builder, use the audience filters to select by RFM segment, store, city, tier, or custom criteria."
  },
  {
    question: "What's the difference between Active and Total members?",
    answer: "Total members is everyone registered. Active members are those who transacted within the last 90 days."
  }
];

export default function HelpCenter() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState('getting-started');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const filteredSections = helpSections.map(section => ({
    ...section,
    articles: section.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.articles.length > 0 || searchQuery === '');

  const renderMarkdown = (content) => {
    // Simple markdown rendering
    return content
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('## ')) {
          return <h2 key={i} className="text-xl font-bold mt-6 mb-3">{line.replace('## ', '')}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={i} className="text-lg font-semibold mt-4 mb-2">{line.replace('### ', '')}</h3>;
        }
        if (line.startsWith('- **')) {
          const match = line.match(/- \*\*(.+?)\*\*: (.+)/);
          if (match) {
            return (
              <li key={i} className="ml-4 mb-1">
                <span className="font-semibold">{match[1]}</span>: {match[2]}
              </li>
            );
          }
        }
        if (line.startsWith('- ')) {
          return <li key={i} className="ml-4 mb-1">{line.replace('- ', '')}</li>;
        }
        if (line.match(/^\d+\. /)) {
          return <li key={i} className="ml-4 mb-1 list-decimal">{line.replace(/^\d+\. /, '')}</li>;
        }
        if (line.trim() === '') {
          return <br key={i} />;
        }
        return <p key={i} className="mb-2">{line}</p>;
      });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="help-center">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Help Center</h1>
                  <p className="text-xs text-muted-foreground">Learn how to use Fundle Brain</p>
                </div>
              </div>
            </div>
            <div className="w-80">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="help-search"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-1 sticky top-24">
              {filteredSections.map((section) => (
                <div key={section.id}>
                  <button
                    onClick={() => {
                      setExpandedSection(expandedSection === section.id ? null : section.id);
                      setSelectedArticle(null);
                    }}
                    className={cn(
                      "w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors",
                      expandedSection === section.id ? "bg-primary/10" : "hover:bg-muted"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <section.icon className={cn("w-5 h-5", section.color)} />
                      <span className="font-medium">{section.title}</span>
                    </div>
                    {expandedSection === section.id ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  
                  {expandedSection === section.id && (
                    <div className="ml-8 mt-1 space-y-1">
                      {section.articles.map((article) => (
                        <button
                          key={article.id}
                          onClick={() => setSelectedArticle(article)}
                          className={cn(
                            "w-full p-2 rounded-md text-left text-sm transition-colors",
                            selectedArticle?.id === article.id 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-muted text-muted-foreground hover:text-foreground"
                          )}
                        >
                          {article.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {selectedArticle ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">{selectedArticle.title}</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm dark:prose-invert max-w-none">
                  {renderMarkdown(selectedArticle.content)}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-8">
                {/* Quick Links */}
                <div>
                  <h2 className="text-lg font-semibold mb-4">Quick Links</h2>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { icon: LayoutDashboard, title: 'Command Center', desc: 'Main dashboard overview', section: 'dashboards', article: 'command-center' },
                      { icon: MessageSquare, title: 'AI Conversation', desc: 'Chat with your data', section: 'ai-features', article: 'ai-conversation' },
                      { icon: Store, title: 'Store Performance', desc: 'Analyze store metrics', section: 'dashboards', article: 'store-performance' },
                      { icon: Users, title: 'RFM Analysis', desc: 'Customer segmentation', section: 'dashboards', article: 'rfm-churn' },
                      { icon: Megaphone, title: 'Campaign Builder', desc: 'Create campaigns', section: 'campaigns', article: 'campaign-builder' },
                      { icon: Settings, title: 'User Management', desc: 'Manage users', section: 'admin', article: 'user-management' },
                    ].map((item, idx) => (
                      <Card 
                        key={idx} 
                        className="cursor-pointer hover:border-primary/50 transition-colors"
                        onClick={() => {
                          setExpandedSection(item.section);
                          const section = helpSections.find(s => s.id === item.section);
                          const article = section?.articles.find(a => a.id === item.article);
                          if (article) setSelectedArticle(article);
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <item.icon className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-medium">{item.title}</h3>
                              <p className="text-sm text-muted-foreground">{item.desc}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* FAQ Section */}
                <div>
                  <h2 className="text-lg font-semibold mb-4">Frequently Asked Questions</h2>
                  <Card>
                    <CardContent className="p-0 divide-y">
                      {faqItems.map((faq, idx) => (
                        <div key={idx}>
                          <button
                            onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                            className="w-full p-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors"
                          >
                            <span className="font-medium pr-4">{faq.question}</span>
                            {expandedFaq === idx ? (
                              <ChevronDown className="w-4 h-4 shrink-0" />
                            ) : (
                              <ChevronRight className="w-4 h-4 shrink-0" />
                            )}
                          </button>
                          {expandedFaq === idx && (
                            <div className="px-4 pb-4 text-muted-foreground">
                              {faq.answer}
                            </div>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {/* Video Training Section */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-violet-500/20">
                      <GraduationCap className="w-5 h-5 text-violet-500" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold">Interactive Video Training</h2>
                      <p className="text-sm text-muted-foreground">AI-narrated walkthroughs for each dashboard</p>
                    </div>
                  </div>
                  <TrainingMenu />
                </div>

                {/* Contact Support */}
                <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
                  <CardContent className="p-6 text-center">
                    <HelpCircle className="w-12 h-12 mx-auto mb-4 text-primary" />
                    <h3 className="text-lg font-semibold mb-2">Still need help?</h3>
                    <p className="text-muted-foreground mb-4">
                      Can't find what you're looking for? Our support team is here to help.
                    </p>
                    <Button>Contact Support</Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
